import React, { useMemo } from "react";

import { useTypedSelector } from "../../../redux/store";
import { hasAccess } from "../../../access";

/**
 * AccessGuard component checks whether the current user has access to a specific resource
 * based on their role. It also optionally checks an additional boolean condition to control visibility.
 *
 * @param {string} accessName - The name of the access level or permission being checked.
 * @param {boolean} [canShow=true] - Optional external condition; must also be true for rendering to occur.
 * @param {boolean} [fallbackWrapper=false] - Optional flag to return an empty div instead of null when access is denied.
 * @param {React.ReactNode} children - The content to render if access is granted.
 *
 * @returns {React.ReactNode} - Renders children if access and condition are satisfied, otherwise null or an empty div.
 */
const AccessGuard: React.FC<AccessGuard.Props> = ({
	accessName,
	canShow = true,
	fallbackWrapper = false,
	children,
}) => {
	// Get the current user's role from the Redux store
	const personalRole = useTypedSelector(
		(state) => state.account.personalRole,
	);

	// Check if the user has the required access based on their role
	const checkAccessRole = useMemo(
		() => hasAccess(accessName, personalRole),
		[accessName, personalRole],
	);

	// Combine access check and the optional visibility condition
	const checkAllShow = useMemo(
		() => checkAccessRole && canShow,
		[canShow, checkAccessRole],
	);

	// If access is denied and fallbackWrapper is true, render an empty div
	// If access is denied and fallbackWrapper is false, render null
	if (!checkAllShow) {
		if (fallbackWrapper) {
			return <div />; // Return an empty div when fallbackWrapper is true
		}
		return null; // Return null when access is denied and fallbackWrapper is false
	}

	// If access is granted, render the children
	return <>{children}</>;
};

declare namespace AccessGuard {
	interface Props {
		/** The access name or permission string that needs to be checked */
		accessName: string;

		/** Optional condition to additionally control rendering */
		canShow?: boolean;

		/** Optional flag to return a fallback div when access is denied (defaults to false) */
		fallbackWrapper?: boolean;

		/** The child components to render if access is granted */
		children: React.ReactNode;
	}
}

export default AccessGuard;
